import PropertyCardItem from "../../components/card-items/property-card-item/property-card-item.component";
import { useEffect, useMemo, useState } from "react";
import { getPropertyList } from "../../api/wordpress";
import PageLoader from "../../components/loaders/page-loader.component";
import { isArray } from "../../utils/functions";
import BtnLoader from "../../components/loaders/btn-loader.component";

const formatDateLabel = (value, now = new Date()) => {
  if (!value) return "Unknown date";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateOnly.getTime() === today.getTime()) return "Today";
  if (dateOnly.getTime() === yesterday.getTime()) return "Yesterday";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "numeric",
    year: "2-digit",
  });
};

const getPropertyDate = (property) => {
  if (!property) return null;

  const directKeys = [
    "date",
    "modified",
    "date_gmt",
    "modified_gmt",
    "date_created",
    "created",
    "created_at",
    "post_date",
    "post_date_gmt",
  ];

  for (const key of directKeys) {
    if (property[key]) return property[key];
  }

  if (property?.acf && typeof property.acf === "object") {
    for (const [key, value] of Object.entries(property.acf)) {
      if (value && /date|created|submitted|timestamp/i.test(key)) {
        return value;
      }
    }
  }

  if (property?.meta?.createdAt) return property.meta.createdAt;
  if (property?.meta?.created) return property.meta.created;
  if (property?.attributes?.createdAt) return property.attributes.createdAt;

  return null;
};

const getDateBucketKey = (value) => {
  if (!value) return "unknown";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const filterPropertiesByDate = (properties = [], filter = "all", now = new Date()) => {
  if (!filter || filter === "all") return properties;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const last7Days = new Date(today);
  last7Days.setDate(today.getDate() - 6);
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  return properties.filter((property) => {
    const sourceDate = getPropertyDate(property);
    if (!sourceDate) return false;

    const date = new Date(sourceDate);
    if (Number.isNaN(date.getTime())) return false;

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    switch (filter) {
      case "today":
        return dateOnly.getTime() === today.getTime();
      case "yesterday":
        return dateOnly.getTime() === yesterday.getTime();
      case "last7Days":
        return dateOnly >= last7Days && dateOnly <= today;
      case "thisMonth":
        return dateOnly >= thisMonthStart && dateOnly <= today;
      default:
        return true;
    }
  });
};

export const sortProperties = (properties = [], order = "newest") => {
  const sorted = [...properties];

  sorted.sort((a, b) => {
    const aDate = new Date(getPropertyDate(a) || 0).getTime();
    const bDate = new Date(getPropertyDate(b) || 0).getTime();

    if (order === "oldest") return aDate - bDate;
    return bDate - aDate;
  });

  return sorted;
};

export const groupPropertiesByDate = (properties = [], now = new Date()) => {
  const groupsMap = new Map();
  const sorted = sortProperties(properties, "newest");

  sorted.forEach((property) => {
    const sourceDate = getPropertyDate(property);
    const bucketKey = getDateBucketKey(sourceDate);

    if (!bucketKey || bucketKey === "unknown") return;

    if (!groupsMap.has(bucketKey)) {
      groupsMap.set(bucketKey, {
        key: bucketKey,
        label: formatDateLabel(sourceDate, now),
        items: [],
      });
    }

    groupsMap.get(bucketKey).items.push(property);
  });

  return Array.from(groupsMap.values());
};

const AdminApprovalsListScreen = () => {
  const [queryParams] = useState({ per_page: 100, page: 1, status: "pending", context: "edit" });
  const [btnloader, setBtnLoader] = useState(false);
  const [propertyList, setPropertylist] = useState([]);
  const [propertyLoader, setPropertyLoader] = useState(true);
  const [dateFilter, setDateFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem("user"))?.token;

    document.title = "Pending Approvals";

    const propertyListRequest = async () => {
      setPropertyLoader(true);
      const response = await getPropertyList(token, queryParams);

      let arr = [];
      if (!isArray(response.data)) {
        if (Number.isInteger(Number(Object.keys(response.data)[0]))) {
          arr = Object.keys(response.data).map((key) => {
            return response.data[key];
          });
        } else {
          arr[0] = response.data;
        }
      } else {
        arr = response.data;
      }

      setPropertylist(arr);
      setPropertyLoader(false);
      setBtnLoader(false);
    };

    propertyListRequest().catch((error) => {
      console.log(error);
    });
  }, [queryParams]);

  const filteredProperties = useMemo(() => {
    const filtered = filterPropertiesByDate(propertyList, dateFilter);
    return sortProperties(filtered, sortOrder);
  }, [dateFilter, propertyList, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / pageSize));
  const visibleProperties = useMemo(() => {
    const end = currentPage * pageSize;
    return filteredProperties.slice(0, end);
  }, [currentPage, filteredProperties]);

  const groupedProperties = groupPropertiesByDate(visibleProperties);

  return (
    <div className="property-list-screen md:pr-8 mb-5 flex flex-col gap-5">
      <div className="bg-blue-50 border-l-4 border-primary p-4 mb-4 rounded-r-md">
        <h2 className="text-xl font-bold text-primary">Pending Property Approvals</h2>
        <p className="text-sm text-gray-700 mt-1">Review the properties below. Click View & Approve to inspect the video tour and publish the listing to the public feed.</p>
      </div>

      {propertyLoader ? (
        <PageLoader size="150px" className="p-10 w-full flex justify-center" />
      ) : (
        <>
          <div className="rounded-xl border border-blue-200 bg-blue-50/80 p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
                <label className="text-sm font-medium text-gray-700">
                  <span className="mr-2">Date</span>
                  <select
                    value={dateFilter}
                    onChange={(event) => {
                      setDateFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm"
                  >
                    <option value="all">All time</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7Days">Last 7 days</option>
                    <option value="thisMonth">This month</option>
                  </select>
                </label>

                <label className="text-sm font-medium text-gray-700">
                  <span className="mr-2">Sort</span>
                  <select
                    value={sortOrder}
                    onChange={(event) => {
                      setSortOrder(event.target.value);
                      setCurrentPage(1);
                    }}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm"
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </label>
              </div>

              <button
                type="button"
                onClick={() => {
                  setDateFilter("all");
                  setSortOrder("newest");
                  setCurrentPage(1);
                }}
                className="inline-flex items-center justify-center rounded-md border border-primary/20 bg-white px-3 py-2 text-sm font-semibold text-primary hover:bg-blue-100"
              >
                Reset filters
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-500">
            Showing {visibleProperties.length} of {filteredProperties.length} pending approvals
          </p>

          {propertyList.length > 0 ? (
            <div className="flex flex-col gap-6">
              {groupedProperties.map((group) => (
                <div key={group.key}>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 mb-3">
                    {group.label}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {group.items.map((item) => (
                      <PropertyCardItem key={item.id} data={item} isAdminView={true} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center text-gray-500">No pending properties for review.</div>
          )}
          {filteredProperties.length > pageSize && currentPage < totalPages && (
            <div className="flex justify-center w-full mt-5 mb-5">
              <button
                className="pagination bg-blue-100 header-font text-primary py-1 px-5 text-sm rounded-full hover:bg-blue-200"
                onClick={() => {
                  setCurrentPage((prev) => prev + 1);
                  setBtnLoader(true);
                  setTimeout(() => setBtnLoader(false), 250);
                }}
              >
                {btnloader && <BtnLoader color={true} />}
                Load More
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminApprovalsListScreen;